import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import { isEqual, omit } from "lodash";
import { Chip, InputAdornment, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import PropTypes from "prop-types";

import Stepper from "common/Stepper";
import BreadCrumb from "Components/Common/BreadCrumb";
import PremiseRemovalDetail from "./PremiseRemovalDetail";
import Declaration from "Components/Declaration";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import AddressForPower from "Components/AddressForPower";
import { applicationTypes, minHtContractDemandLoad } from "common/constant";
import Backdrop from "Components/Common/Backdrop";

import { setValues } from "slices/premiseRemoval/reducer";
import { useValidationSchemaForPremiseRemoval as useValidationSchema } from "hooks";

import { convertDocumentKeys, generateFormData } from "utils";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

import {
  useLazyApplicationDetailQuery,
  usePostApplicationDetailsMutation,
} from "apis/ApplicationRequestDetail";
import { useLazyConsumerByIdQuery } from "apis/User";
import { useFloorQuery } from "apis/Floor";
import { myRequestRoute } from "Routes/constants";

const filterEmptyStrings = (obj) => {
  return Object.entries(obj).reduce((res, [key, value]) => {
    if (value instanceof File) {
      return { ...res, [key]: value };
    }
    if (Array.isArray(value)) {
      return { ...res, [key]: value.map((val) => filterEmptyStrings(val)) };
    }
    if (value && typeof value === "object") {
      return { ...res, [key]: filterEmptyStrings(obj[key]) };
    }
    if (value !== "") {
      return { ...res, [key]: value };
    }
    return res;
  }, {});
};

const PremiseRemoval = ({ applicationId, handleClose, t }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [applicationData, setApplicationData] = useState({});
  const user = useSelector((state) => state.Login.user);

  const navigate = useNavigate();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: consumerData } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyConsumerByIdQuery();
  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();
  const { data: { data: floors } = {} } = useFloorQuery();

  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const getValidationSchema = useValidationSchema();

  const dispatch = useDispatch();

  const floorOptions = useMemo(
    () =>
      floors?.map((floor) => ({ label: floor.name, value: floor.id })) ?? [],
    [floors]
  );

  const initialValues = useMemo(
    () => ({
      id: applicationId ?? 0,
      utilityConsumerId:
        consumerData?.id ?? applicationData?.utilityConsumerId ?? "",
      consumerName: consumerData?.consumerName ?? "",
      tariffName: consumerData?.tariffName ?? "",
      utilityId: 1,
      consumerNumber:
        applicationData?.consumerNumber ?? consumerData?.consumerNumber ?? "",

      applicationTypeId: applicationTypes["Premise Removal"],
      applicant: {
        id: applicationData?.applicant?.id ?? consumerData?.applicant?.id ?? 0,
        applicantTypeId: consumerData?.applicant?.applicantType?.id ?? "",
        applicantName: consumerData?.applicant?.applicantName ?? "",
        panNumber:
          applicationData?.applicant?.panNumber ??
          consumerData?.applicant?.panNumber ??
          "",
        gstNumber:
          applicationData?.applicant?.gstNumber ??
          consumerData?.applicant?.gstNumber ??
          "",
        occupancyTypeId: consumerData?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered:
          applicationData?.applicant?.isGSTRegistered ??
          consumerData?.applicant?.isGSTRegistered ??
          "",
        addressType: consumerData?.applicant?.addressType ?? "",
        correspondenceAddress:
          consumerData?.applicant?.correspondenceAddress ?? "",
        city: consumerData?.applicant?.city ?? "",
        pinCode: consumerData?.applicant?.pinCode ?? "",
        email: consumerData?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: consumerData?.applicant?.mobileNumber ?? "",
        landLine: consumerData?.applicant?.landLine ?? "",
        alternateEmail: consumerData?.applicant?.alternateEmail
          ? consumerData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          consumerData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        isEditablePerson:
          applicationData?.authorizedPerson?.isEditablePerson ?? false,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        // gender: applicationData?.authorizedPerson?.gender ?? "",
        // ageCategory: applicationData?.authorizedPerson?.ageCategory ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        id: applicationData?.utilityAddress?.id ?? 0,
        premiseTypeId: consumerData?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          consumerData?.utilityAddress?.premiseUsagePurpose ?? "",
        premisesAreaId: consumerData?.utilityAddress?.premiseArea?.id ?? "",
        zoneName: consumerData?.utilityAddress?.zone?.name ?? "",
        otherZone: "",
        blockName: consumerData?.utilityAddress?.block?.name ?? "",
        otherBlock: "",
        roadName: consumerData?.utilityAddress?.road?.name ?? "",
        otherRoad: "",
        buildingName: consumerData?.utilityAddress?.building?.name ?? "",
        otherBuilding: "",
        floors: [],
        unitName:
          applicationData?.utilityAddress?.unitName ??
          consumerData?.utilityAddress?.unitName ??
          "",
        nearestLandmark: consumerData?.utilityAddress?.nearestLandmark ?? "",
        isBPL: consumerData?.utilityAddress?.isBPL ?? "",
        // isRural: consumerData?.utilityAddress?.isRural ?? "",
      },
      connectionLoad: {
        id: applicationData?.connectionLoad?.id ?? 0,
        backupRequired:
          !!applicationData?.connectionLoad?.backupkVA ||
          !!consumerData?.connectionLoad?.backupkVA,
        backupkVA:
          applicationData?.connectionLoad?.backupkVA ??
          consumerData?.connectionLoad?.backupkVA ??
          "",
        powerRequirementUnit:
          (applicationData?.connectionLoad?.totalPowerRequirement ??
            consumerData?.connectionLoad?.totalPowerRequirement) <= 99
            ? 1
            : 2 ?? "",
        loadDetails:
          applicationData?.connectionLoad?.loadDetail ??
          consumerData?.connectionLoad?.loadDetail ??
          "",
        supplyVoltage: +applicationData?.connectionLoad?.supplyVoltage
          ? +applicationData.connectionLoad.supplyVoltage
          : +consumerData?.connectionLoad?.supplyVoltage
          ? +consumerData.connectionLoad.supplyVoltage
          : "",
        supplyVoltageUnit:
          applicationData?.connectionLoad?.supplyVoltageUnit ??
          consumerData?.connectionLoad?.supplyVoltageUnit ??
          "",
        powerRequirement:
          applicationData?.connectionLoad?.powerRequirement ??
          consumerData?.connectionLoad?.totalPowerRequirement ??
          "",
        timeShift:
          applicationData?.connectionLoad?.timeShiftEnum ??
          consumerData?.connectionLoad?.timeShiftEnum ??
          "",
        tention: applicationData?.connectionLoad?.totalPowerRequirement
          ? applicationData.connectionLoad.totalPowerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : consumerData?.connectionLoad?.totalPowerRequirement
          ? consumerData.connectionLoad.totalPowerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : "",
        isGreenPowerRequired: !!consumerData?.connectionLoad?.greenPower,
        greenPower:
          applicationData?.connectionLoad?.greenPower ??
          consumerData?.connectionLoad?.greenPower ??
          "",
        averageConsumption:
          applicationData?.connectionLoad?.averageConsumption ??
          consumerData?.connectionLoad?.averageConsumption ??
          "",
        decreaseOfLoad:
          applicationData?.connectionLoad?.decreaseOfLoad ??
          consumerData?.connectionLoad?.decreaseOfLoad ??
          "",
        totalPowerRequirement:
          applicationData?.connectionLoad?.totalPowerRequirement ??
          consumerData?.connectionLoad?.totalPowerRequirement ??
          0,
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      extraValues: {
        premiseTypeId: consumerData?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          consumerData?.utilityAddress?.premiseUsagePurpose ?? "",
        requiredDocumentsCount: 0,
        isPanNumber: !!consumerData?.applicant?.panNumber,
        isGstNumber: !!consumerData?.applicant?.gstNumber,
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
      },
      inProgressApplicationId: consumerData?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        consumerData?.isAllApplicationsProcessCompleted ?? false,
      payableBillAmount: consumerData?.payableBillAmount ?? 0,
      isIGSTExempted: consumerData?.isIGSTExempted ?? false,
    }),
    [applicationData, consumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getValidationSchema(currentStep),

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createPremiseRemoval(values);
      } else {
        // validate if use have fill changeable details in form or not
        const formikValuesFloors = cloneDeep(values.utilityAddress.floors).sort(
          (a, b) => a.label.localeCompare(b.label)
        );
        const consumerDataFloors =
          consumerData?.utilityAddress?.floors
            ?.map(({ id, name }) => ({
              label: name,
              value: id,
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) ?? [];
        if (
          isEqual(formikValuesFloors, consumerDataFloors) &&
          currentStep === 2
        ) {
          toast.warning("Please Remove any address");
        } else if (
          values?.inProgressApplicationId !== applicationData?.id &&
          !values.isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (values.payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${values.payableBillAmount} RS. Please pay first to continue.`
          );
        } else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      }
    },
  });

  const {
    handleSubmit,
    values,
    setFieldValue,
    initialValues: formikInitialValues,
  } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const floorFieldProps = useMemo(
    () => ({
      options: [],
      freeSolo: true,
      renderInput: (params) => (
        <TextField
          {...params}
          label={t("Floors")}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment sx={{ height: "2rem" }} position="end">
                <RefreshIcon
                  className="cursor-pointer"
                  onClick={() =>
                    setFieldValue(
                      "utilityAddress.floors",
                      consumerData?.utilityAddress?.floors?.map(
                        ({ id, name }) => ({
                          label: name,
                          value: id,
                        })
                      )
                    )
                  }
                />
              </InputAdornment>
            ),
          }}
        />
      ),
      renderTags: (tagValue, getTagProps) => (
        <>
          {tagValue.map((option, index) => (
            <Chip
              className="m-0"
              key={option.value}
              label={option.label}
              {...getTagProps({ index })}
            />
          ))}
        </>
      ),
    }),
    [consumerData?.utilityAddress?.floors, values.utilityAddress.floors]
  );

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Premise Removal"),
        isCompleted: currentStep >= 1,
        component: <PremiseRemovalDetail formik={formik} />,
      },
      {
        id: 2,
        name: t("Authorized Person Details"),
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: t("Address for Power Connection"),
        isCompleted: currentStep >= 3,
        component: (
          <AddressForPower
            isDisable
            floorFieldProps={floorFieldProps}
            floorOptions={floorOptions}
            formik={formik}
          />
        ),
      },
      {
        id: 4,
        name: t("Document to Submit"),
        isCompleted: currentStep >= 4,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 5,
        name: t("Declaration"),
        isCompleted: currentStep === 5,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik, floorOptions]
  );

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 3 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const getCustomerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        applicationId &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  const getApplicationData = async (applicationId) => {
    try {
      const res = await getApplicationDetails(applicationId);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  const createPremiseRemoval = async (values) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";

    const otherZone = values.utilityAddress.otherZone;
    const otherBlock = values.utilityAddress.otherBlock;
    const otherRoad = values.utilityAddress.otherRoad;
    const otherBuilding = values.utilityAddress.otherBuilding;
    const cityId = values.applicant.city?.id ?? "";
    const id = values.applicant.id;

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(payloadWithUpdatedDocumentKeys, [
      "applicant.city",
      "utilityAddress.otherZone",
      "utilityAddress.otherBlock",
      "utilityAddress.otherRoad",
      "utilityAddress.otherBuilding",
      "tariffName",
      "inProgressApplicationId",
      "isAllApplicationsProcessCompleted",
      "payableBillAmount",
      "consumerNumber",
      "consumerName",
      "connectionLoad.tention",
      "connectionLoad.backupRequired",
      "authorizedPerson.isEditablePerson",
      "connectionLoad.isGreenPowerRequired",
      "authorizedPerson.isAuthorizedPerson",
      "authorizedPerson.isUnRegisteredPerson",
      "declaration.isConsumerDuesOutstanding",
      "declaration.isPremisesDuesOutstanding",
      "declaration.isFirmDuesOutstanding",
      "documents",
      "extraValues",
      "isIGSTExempted",
      imageKeyToRemove,
    ]);

    const currentGstNumber = applicationId
      ? applicationData?.applicant?.gstNumber
      : consumerData?.applicant?.gstNumber;

    const currentPanNumber = applicationId
      ? applicationData?.applicant?.panNumber
      : consumerData?.applicant?.panNumber;

    const filteredValues = filterEmptyStrings({
      ...applicationDetails,
      applicant: {
        ...applicationDetails.applicant,
        alternateEmail: applicationDetails.applicant.alternateEmail.length
          ? applicationDetails.applicant.alternateEmail.join(", ")
          : "",
        cityId,
        id:
          applicationDetails.applicant.gstNumber !== (currentGstNumber ?? "") ||
          applicationDetails.applicant.panNumber !== (currentPanNumber ?? "")
            ? 0
            : id,
      },
      utilityAddress: {
        ...applicationDetails.utilityAddress,
        zoneName:
          payloadWithUpdatedDocumentKeys.utilityAddress.zoneName === "other"
            ? otherZone
            : payloadWithUpdatedDocumentKeys.utilityAddress.zoneName,
        blockName:
          payloadWithUpdatedDocumentKeys.utilityAddress.blockName === "other"
            ? otherBlock
            : payloadWithUpdatedDocumentKeys.utilityAddress.blockName,
        roadName:
          payloadWithUpdatedDocumentKeys.utilityAddress.roadName === "other"
            ? otherRoad
            : payloadWithUpdatedDocumentKeys.utilityAddress.roadName,
        buildingName:
          payloadWithUpdatedDocumentKeys.utilityAddress.buildingName === "other"
            ? otherBuilding
            : payloadWithUpdatedDocumentKeys.utilityAddress.buildingName,
        floors: payloadWithUpdatedDocumentKeys.utilityAddress.floors.map(
          ({ label, value }) => ({
            id: value,
            name: label,
          })
        ),
      },
    });

    // remove entries that contain only id field
    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          Array.isArray(value) ||
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
    });
    try {
      await submitApplication(formData);
      if (!applicationId) {
        navigate(myRequestRoute);
      }
      handleClose();
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(setValues(values));
  }, [values, dispatch]);

  // set initial floors
  useEffect(() => {
    const initialFloors =
      applicationData?.utilityAddress?.floors ??
      consumerData?.utilityAddress?.floors ??
      [];

    if (floorOptions.length && initialFloors.length) {
      setFieldValue(
        "utilityAddress.floors",
        floorOptions.filter((option) =>
          initialFloors.some((floor) => floor.id === option.value)
        )
      );
    }
  }, [floorOptions, applicationData, consumerData]);

  useEffect(() => {
    if (applicationId) {
      getApplicationData(applicationId);
      // setCompletedSteps(steps.length);
    }
  }, [applicationId]);

  useEffect(() => {
    if (values.utilityConsumerId) {
      getCustomerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  return (
    <div className={!applicationId ? "h-100" : ""}>
      <Backdrop
        open={
          isApplicationDataLoading ||
          isConsumerDetailsLoading ||
          isApplicationDataFetching ||
          isConsumerDataFetching ||
          (!!applicationId && !consumerData)
        }
      />
      {!applicationId && (
        <BreadCrumb
          title={t("Premise Removal")}
          pageTitle={t("Power Applications")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          validateDocument={validateDocument}
          isLoading={isLoading}
          isDraftDisabled={true}
          formik={formik}
        />
      </div>
    </div>
  );
};

PremiseRemoval.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(PremiseRemoval);
